/**
 * Who may reach face scan, and when the register offers it.
 *
 * `FACE_SCAN_ROLES` is the router's `RequireRole` list for
 * `/attendance/face-scan`. It is NOT optional: `role-access.ts` allow-lists
 * `/attendance` by prefix, which also admits PARENT and STUDENT (they get the
 * per-child view on that route), so without this gate a parent could open the
 * scanner by URL. It mirrors the API's `authorize()` on the face-scan routes —
 * STAFF can take a register but cannot run a scan.
 */
export const FACE_SCAN_ROLES: string[] = ["TEACHER", "PRINCIPAL", "ADMIN", "CHAIRMAN", "DIRECTOR"];

/** Admin tier — may record paper consent for any class. */
export const FACE_ADMIN_ROLES: readonly string[] = ["PRINCIPAL", "ADMIN", "CHAIRMAN", "DIRECTOR"];

export function canRunFaceScan(role: string | null | undefined): boolean {
  return !!role && FACE_SCAN_ROLES.includes(role);
}

export function canRecordPaperConsent(role: string | null | undefined): boolean {
  return !!role && FACE_ADMIN_ROLES.includes(role);
}

/** Admin tier — may switch "Face scan for students" on or off for the school. */
export function canManageFaceScan(role: string | null | undefined): boolean {
  return !!role && FACE_ADMIN_ROLES.includes(role);
}

export interface FaceScanEntryInput {
  role: string | null | undefined;
  /** Register selection: a real class id, not "all". */
  classId: string;
  /** Selected section; "" only when the class has none. */
  section: string;
  /** True when the class has sections, so one MUST be picked. */
  classHasSections: boolean;
  /** Register date and today, both `YYYY-MM-DD` (IST). */
  date: string;
  today: string;
  /** The register's own gate (role + academic year already started). */
  canTakeAttendance: boolean;
  /** `studentFaceScanEnabled` from the school's attendance settings. */
  enabled: boolean | undefined;
}

/**
 * The "Face scan" button on the register shows only for ONE class + section,
 * on TODAY's register, to someone who can take attendance AND run a scan, in a
 * school that switched the feature on. (Recognition is refused server-side for
 * any other date, so offering it elsewhere would only lead to an error.)
 */
export function faceScanEntryVisible(i: FaceScanEntryInput): boolean {
  if (!i.enabled || !i.canTakeAttendance || !canRunFaceScan(i.role)) return false;
  if (!i.classId || i.classId === "all") return false;
  if (i.classHasSections && !i.section) return false;
  return i.date !== "" && i.date === i.today;
}

export function faceScanHref(input: { classId: string; section: string; period: "AM" | "PM" | "FULL" }): string {
  const q = new URLSearchParams({ classId: input.classId, section: input.section, period: input.period });
  return `/attendance/face-scan?${q.toString()}`;
}

/** A class + section the register can hand to face scan (real class; a section when the class has any). */
export function hasFaceScanTarget(i: Pick<FaceScanEntryInput, "classId" | "section" | "classHasSections">): boolean {
  if (!i.classId || i.classId === "all") return false;
  return !(i.classHasSections && !i.section);
}

/**
 * Why the "Face scan" entry is not showing, when the school has it ON and the
 * user could run a scan: a one-line muted hint instead of an unexplained gap.
 * "pick" = no single class / section yet; "today" = a past or future date.
 * Null when the entry shows, or when nothing useful can be said (switch off or
 * still unknown, a role that cannot scan, a register the user cannot take).
 */
export function faceScanHint(i: FaceScanEntryInput): "pick" | "today" | null {
  if (i.enabled !== true || !i.canTakeAttendance || !canRunFaceScan(i.role)) return null;
  if (!hasFaceScanTarget(i)) return "pick";
  if (i.date !== "" && i.date !== i.today) return "today";
  return null;
}

/** Faces tab (record consent + enrol) for the picked class and section. */
export function faceScanSetupHref(input: { classId: string; section: string }): string {
  const q = new URLSearchParams({ tab: "faces", classId: input.classId });
  if (input.section) q.set("section", input.section);
  return `/attendance/face-scan?${q.toString()}`;
}
